import { Link, useParams } from "react-router";
import { Aviso, Botao, Cartao, Chip } from "../componentes/ui";
import { EvolucaoDaSaude } from "../componentes/sucesso/EvolucaoDaSaude";
import { ListaDeAlertas } from "../componentes/sucesso/PainelDeAlertas";
import { Selecionar } from "../componentes/sucesso/Selecionar";
import { mensagemDoConsole } from "../nucleo/console";
import { podeNaSessao, useSessao } from "../nucleo/sessao";
import {
  CHIP_DA_FAIXA,
  ROTULO_DA_FAIXA,
  ROTULO_DO_SINAL,
  SINAIS,
  descreverSinal,
  useConfiguracaoDaSaude,
  useDefinirResponsavelDeCS,
  useRecalcularSaude,
  useResponsaveisDeCS,
  useSaudeDaOrganizacao,
  type SaudeDaOrganizacao as Saude,
} from "../nucleo/sucesso";
import { useTitulo } from "../nucleo/titulo";

function nota(valor: number | null): string {
  return valor === null ? "sem dado" : valor.toFixed(0);
}

/** A saúde de UMA organização: o score, o que o compõe, a evolução e os alertas. */
export function SaudeDaOrganizacao() {
  const { organizacaoId = "" } = useParams();
  const sessao = useSessao();
  const permitido = podeNaSessao(sessao.data, "sucesso:ver");
  const saude = useSaudeDaOrganizacao(organizacaoId, permitido);
  useTitulo(saude.data ? `Saúde de ${saude.data.organizacao.nome}` : "Saúde do cliente");

  if (!sessao.data) {
    return (
      <p className="lead" role="status">
        Verificando o seu acesso…
      </p>
    );
  }
  if (!permitido) {
    return (
      <Aviso tom="marca" data-testid="sucesso-sem-permissao">
        A saúde dos clientes é acompanhada pela equipe da GEOvendas.
      </Aviso>
    );
  }
  if (saude.isPending) return <p role="status">Carregando a saúde do cliente…</p>;
  if (saude.isError) {
    return (
      <>
        <Aviso tom="erro">{mensagemDoConsole(saude.error)}</Aviso>
        <p className="mt-3">
          <Link to="/admin/sucesso">Voltar à carteira</Link>
        </p>
      </>
    );
  }
  return (
    <Conteudo
      saude={saude.data}
      organizacaoId={organizacaoId}
      podeGerenciar={podeNaSessao(sessao.data, "sucesso:gerenciar")}
    />
  );
}

function Conteudo({
  saude,
  organizacaoId,
  podeGerenciar,
}: {
  saude: Saude;
  organizacaoId: string;
  podeGerenciar: boolean;
}) {
  const configuracao = useConfiguracaoDaSaude();
  const equipe = useResponsaveisDeCS(podeGerenciar);
  const definir = useDefinirResponsavelDeCS(organizacaoId);
  const recalcular = useRecalcularSaude();

  return (
    <div data-testid="saude-da-organizacao">
      <h1 className="titulo-pagina">{saude.organizacao.nome}</h1>
      <p className="lead mb-4">
        <Link to="/admin/sucesso">Voltar à carteira</Link>
        {" · "}
        <Link to={`/admin/cadastros/organizacoes/${saude.organizacao.id}`}>Ver o cadastro</Link>
      </p>

      <Cartao titulo="Hoje" className="mb-4">
        {saude.faixa && saude.score !== null ? (
          <p data-testid="score-atual">
            <Chip estado={CHIP_DA_FAIXA[saude.faixa]}>{ROTULO_DA_FAIXA[saude.faixa]}</Chip>{" "}
            <b style={{ fontSize: "28px" }}>{saude.score}</b> de 100
          </p>
        ) : (
          <Aviso tom="marca" data-testid="sem-retrato">
            A saúde desta organização ainda não foi calculada.
          </Aviso>
        )}
        <div className="mt-3 grid gap-3 md:grid-cols-2">
          {podeGerenciar ? (
            <Selecionar
              rotulo="Responsável de CS"
              value={saude.responsavel?.id ?? ""}
              disabled={definir.isPending}
              onChange={(e) => definir.mutate(e.target.value || null)}
            >
              <option value="">Sem responsável</option>
              {/* Enquanto a equipe não chega, o responsável de hoje já aparece escolhido. */}
              {(equipe.data ?? (saude.responsavel ? [saude.responsavel] : [])).map((pessoa) => (
                <option key={pessoa.id} value={pessoa.id}>
                  {pessoa.nome}
                </option>
              ))}
            </Selecionar>
          ) : (
            <p>
              Responsável de CS: <b>{saude.responsavel?.nome ?? "ninguém ainda"}</b>
            </p>
          )}
          {podeGerenciar ? (
            <div>
              <Botao
                variante="fantasma"
                disabled={recalcular.isPending}
                onClick={() => recalcular.mutate(organizacaoId)}
                data-testid="recalcular-organizacao"
              >
                Recalcular agora
              </Botao>
            </div>
          ) : null}
        </div>
        {definir.isError ? <Aviso tom="erro" className="mt-3">{mensagemDoConsole(definir.error)}</Aviso> : null}
        {recalcular.isError ? <Aviso tom="erro" className="mt-3">{mensagemDoConsole(recalcular.error)}</Aviso> : null}
      </Cartao>

      {Object.keys(saude.sinais).length > 0 ? (
        <Cartao titulo="O que compõe a nota" className="mb-4">
          <div className="tabela-rolagem">
            <table className="tabela" data-testid="sinais-da-saude">
              <caption className="sr-only">Sinais que compõem a nota de saúde</caption>
              <thead>
                <tr>
                  <th scope="col">Sinal</th>
                  <th scope="col">Situação</th>
                  <th scope="col">Nota</th>
                  <th scope="col">Peso</th>
                </tr>
              </thead>
              <tbody>
                {SINAIS.filter((sinal) => saude.sinais[sinal]).map((sinal) => {
                  const item = saude.sinais[sinal];
                  if (!item) return null;
                  return (
                    <tr key={sinal} data-testid={`sinal-${sinal}`} data-entra={item.entra_no_score}>
                      <td>{ROTULO_DO_SINAL[sinal]}</td>
                      <td>{descreverSinal(sinal, item.detalhe)}</td>
                      <td>{nota(item.nota)}</td>
                      <td>
                        {item.peso}
                        {!item.entra_no_score ? (
                          <span className="celula-secundaria">
                            {" "}
                            ({item.peso === 0 ? "fora da conta" : "sem dado, não pesa"})
                          </span>
                        ) : null}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Cartao>
      ) : null}

      <Cartao titulo="Evolução nos últimos 90 dias" className="mb-4">
        <EvolucaoDaSaude
          pontos={saude.evolucao}
          limiteSaudavel={configuracao.data?.limite_saudavel ?? 70}
          limiteAtencao={configuracao.data?.limite_atencao ?? 40}
        />
      </Cartao>

      <Cartao titulo="Quedas de faixa">
        {saude.alertas.length === 0 ? (
          <p>Nenhuma queda de faixa registrada para este cliente.</p>
        ) : (
          <ListaDeAlertas alertas={saude.alertas} semLink />
        )}
      </Cartao>
    </div>
  );
}
